import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import {
  getWorkAreaDriftSummary,
  type WorkAreaDriftState,
  type WorkAreaRecentDriftArtifact,
} from '@/lib/api';

interface WorkAreaDriftPanelProps {
  workAreaId: string;
}

const driftStateRows: { key: WorkAreaDriftState; label: string }[] = [
  { key: 'fresh', label: 'Fresh' },
  { key: 'drifted', label: 'Drifted' },
  { key: 'comment_deleted', label: 'Deleted' },
  { key: 'verifying', label: 'Verifying' },
  { key: 'superseded', label: 'Superseded' },
  { key: 'unpublished', label: 'Unpublished' },
];

function formatDate(value: string | null): string {
  return value ? value.slice(0, 19).replace('T', ' ') : 'Never';
}

function stateVariant(
  state: WorkAreaDriftState
): 'default' | 'destructive' | 'outline' | 'secondary' {
  if (state === 'drifted' || state === 'comment_deleted') return 'destructive';
  if (state === 'fresh') return 'secondary';
  if (state === 'verifying') return 'default';
  return 'outline';
}

function DriftTable({ rows }: { rows: WorkAreaRecentDriftArtifact[] }): React.ReactElement {
  if (rows.length === 0) {
    return <div className="text-sm text-text-secondary">No recent drift.</div>;
  }

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-medium uppercase text-text-tertiary">Recently drifted</h3>
      <div className="overflow-auto rounded-md border border-border">
        <table className="w-full min-w-[44rem] table-fixed text-sm">
          <thead>
            <tr className="border-b border-border text-left text-text-secondary">
              <th className="w-[38%] px-3 py-2 font-medium">Artifact</th>
              <th className="w-[14%] px-3 py-2 font-medium">Type</th>
              <th className="w-[14%] px-3 py-2 font-medium">State</th>
              <th className="w-[20%] px-3 py-2 font-medium">Last checked</th>
              <th className="w-[14%] px-3 py-2 text-right font-medium">Publication</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr
                key={`${row.artifactId}:${row.publicationId ?? 'unpublished'}`}
                className="border-b border-border/60 last:border-0"
              >
                <td className="px-3 py-2">
                  <Link
                    to={`/artifacts/${encodeURIComponent(row.artifactId)}`}
                    className="block truncate font-medium text-primary hover:underline"
                    title={row.title}
                  >
                    {row.title}
                  </Link>
                </td>
                <td className="truncate px-3 py-2 text-text-secondary">{row.type}</td>
                <td className="px-3 py-2">
                  <Badge variant={stateVariant(row.driftState)}>{row.driftState}</Badge>
                </td>
                <td
                  className="truncate px-3 py-2 text-text-secondary"
                  title={row.lastCheckedAt ?? undefined}
                >
                  {formatDate(row.lastCheckedAt)}
                </td>
                <td className="px-3 py-2 text-right">
                  {row.publicationUrl ? (
                    <a
                      href={row.publicationUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline"
                    >
                      Open
                    </a>
                  ) : (
                    <span className="text-text-tertiary">None</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function WorkAreaDriftPanel({ workAreaId }: WorkAreaDriftPanelProps): React.ReactElement {
  const driftQuery = useQuery({
    queryKey: ['work-area-drift-summary', workAreaId],
    queryFn: () => getWorkAreaDriftSummary(workAreaId),
  });

  if (driftQuery.isLoading) {
    return (
      <section className="rounded-md border border-border bg-surface-elevated p-3 text-sm text-text-secondary">
        Loading drift summary…
      </section>
    );
  }

  if (driftQuery.isError) {
    return (
      <section className="rounded-md border border-error/40 bg-error/10 p-3 text-sm text-error">
        Failed to load drift summary
        {driftQuery.error ? `: ${driftQuery.error.message}` : '.'}
      </section>
    );
  }

  const summary = driftQuery.data;
  if (!summary) {
    return (
      <section className="rounded-md border border-border bg-surface-elevated p-3 text-sm text-text-secondary">
        Drift summary unavailable.
      </section>
    );
  }

  return (
    <section className="space-y-3 rounded-md border border-border bg-surface-elevated p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-text-primary">Artifact Drift</h2>
          <p className="text-xs text-text-secondary">{summary.totalArtifacts} artifacts tracked</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {driftStateRows.map(row => (
            <Badge key={row.key} variant={stateVariant(row.key)}>
              {row.label}: {summary.byDriftState[row.key] ?? 0}
            </Badge>
          ))}
        </div>
      </div>
      <DriftTable rows={summary.recentlyDriftedArtifacts} />
    </section>
  );
}
